import { useEffect, useRef, type CSSProperties, type ReactNode } from "react";
import { Check } from "lucide-react";
import { usePreview } from "./useFinishControls";
import type { FinishOption, FinishSwatch } from "../../lib/finishes/catalog";
import { useFinishTile } from "../../lib/finishes/thumbs";
import { BEAM_CEILING, SLAT_BACKING } from "../../lib/render3d/scene/finishes";
import { cn } from "../../lib/cn";

const SKY = "#cfe0ec";

function mask(image: string): CSSProperties {
  return { maskImage: image, WebkitMaskImage: image };
}

/**
 * A finish as a little picture: the tinted scan (flat color until it
 * decodes), with the geometry drawn over it for slats, beams, fences and
 * the rest. Same look on type tiles, swatches, card headers and Looks.
 */
export function FinishArt({ option, color, className }: { option: FinishOption; color: string; className?: string }) {
  const url = useFinishTile(option, color);
  const fill: CSSProperties = url
    ? { backgroundColor: color, backgroundImage: `url(${url})`, backgroundSize: "cover", backgroundPosition: "center" }
    : { backgroundColor: color };
  const layer = (style: CSSProperties) => <div className="absolute inset-0" style={{ ...fill, ...style }} />;
  let art: ReactNode;
  switch (option.geometry) {
    case "slat":
      art = (
        <>
          <div className="absolute inset-0" style={{ background: SLAT_BACKING }} />
          {layer(mask("repeating-linear-gradient(90deg, #000 0 8.5%, transparent 8.5% 12.5%)"))}
        </>
      );
      break;
    case "beams":
      art = (
        <>
          <div className="absolute inset-0" style={{ background: BEAM_CEILING }} />
          {layer(mask("repeating-linear-gradient(180deg, transparent 0 18%, #000 18% 33%)"))}
        </>
      );
      break;
    case "pergola":
      art = (
        <>
          <div className="absolute inset-0" style={{ background: SKY }} />
          {layer(mask("repeating-linear-gradient(90deg, transparent 0 16%, #000 16% 25%)"))}
        </>
      );
      break;
    case "fence":
      art = (
        <>
          <div className="absolute inset-0" style={{ background: SKY }} />
          {layer({ ...mask("repeating-linear-gradient(90deg, #000 0 15%, transparent 15% 16.6%)"), top: "14%" })}
        </>
      );
      break;
    case "railing":
      art = (
        <>
          <div className="absolute inset-0" style={{ background: SKY }} />
          {layer({ ...mask("repeating-linear-gradient(90deg, #000 0 4%, transparent 4% 16.6%)"), top: "30%" })}
          <div className="absolute inset-x-0" style={{ top: "26%", height: "8%", background: color }} />
        </>
      );
      break;
    case "open":
      art = (
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, #b9d3e6, ${SKY} 70%, #e8efe0)` }}>
          <div className="absolute inset-x-[12%] bottom-[22%] border-t-2 border-dashed border-ink-400/60" />
        </div>
      );
      break;
    case "house":
      art = (
        <>
          {layer({})}
          <div className="absolute left-[30%] top-[28%] h-[34%] w-[40%] rounded-[2px] bg-[#cfe0ec] ring-2 ring-white/90" />
        </>
      );
      break;
    case "panelling":
      art = (
        <>
          {layer({})}
          <div className="absolute inset-x-0 top-[40%] h-[5%] bg-white/45 shadow-[0_1px_0_rgba(0,0,0,0.18)]" />
          <div
            className="absolute inset-x-0 bottom-0 top-[45%]"
            style={{ background: "repeating-linear-gradient(90deg, transparent 0 24%, rgba(0,0,0,0.16) 24% 25%, rgba(255,255,255,0.3) 25% 26%)" }}
          />
        </>
      );
      break;
    default:
      art = layer({});
  }
  return (
    <div className={cn("overflow-hidden", className)} aria-hidden>
      {art}
    </div>
  );
}

export function FinishTile({
  option,
  color,
  selected,
  onSelect,
  onPreview,
  testId,
}: {
  option: FinishOption;
  color: string;
  selected: boolean;
  onSelect: () => void;
  onPreview: () => void;
  testId: string;
}) {
  const preview = usePreview(onPreview);
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={option.label}
      title={option.label}
      onClick={onSelect}
      data-testid={testId}
      {...preview}
      className="group flex w-[4.5rem] shrink-0 snap-start flex-col items-center gap-1.5 rounded-2xl p-1 outline-none focus-visible:ring-2 focus-visible:ring-accent-500/60"
    >
      <span
        className={cn(
          "relative block size-16 rounded-xl transition-transform duration-150 motion-reduce:transition-none",
          selected ? "ring-2 ring-accent-500 ring-offset-2 scale-[1.04]" : "ring-1 ring-black/10 group-hover:-translate-y-px group-hover:ring-ink-300"
        )}
      >
        <FinishArt option={option} color={color} className="absolute inset-0 rounded-xl" />
        {selected && (
          <span className="absolute -right-1.5 -top-1.5 inline-flex size-5 items-center justify-center rounded-full bg-accent-500 text-white shadow ring-2 ring-white">
            <Check className="size-3" strokeWidth={3} aria-hidden />
          </span>
        )}
      </span>
      <span className={cn("line-clamp-2 text-center text-caption leading-tight", selected ? "font-semibold text-ink-900" : "text-ink-500")}>
        {option.label}
      </span>
    </button>
  );
}

export function SwatchButton({
  option,
  swatch,
  selected,
  onSelect,
  testId,
}: {
  option: FinishOption;
  swatch: FinishSwatch;
  selected: boolean;
  onSelect: () => void;
  testId?: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={swatch.name}
      title={swatch.name}
      onClick={onSelect}
      data-testid={testId}
      className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-accent-500/60 focus-visible:ring-offset-2"
    >
      <span
        className={cn(
          "relative block size-8 rounded-full transition-transform duration-150 motion-reduce:transition-none",
          selected ? "ring-2 ring-accent-500 ring-offset-2 scale-110" : "ring-1 ring-black/10 hover:scale-105"
        )}
      >
        <FinishArt option={option} color={swatch.hex} className="absolute inset-0 rounded-full" />
      </span>
    </button>
  );
}

/** A plain paint chip (trim). */
export function ColorChip({ swatch, selected, onSelect, testId }: { swatch: FinishSwatch; selected: boolean; onSelect: () => void; testId?: string }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={swatch.name}
      title={swatch.name}
      onClick={onSelect}
      data-testid={testId}
      className={cn(
        "size-7 rounded-full outline-none transition-transform duration-150 focus-visible:ring-2 focus-visible:ring-accent-500/60 focus-visible:ring-offset-2 motion-reduce:transition-none",
        selected ? "ring-2 ring-accent-500 ring-offset-2 scale-110" : "ring-1 ring-black/10 hover:scale-105"
      )}
      style={{ background: swatch.hex }}
    />
  );
}

/**
 * The free color: edits live inside one undo gesture and reports once,
 * when the picker closes.
 */
export function CustomColorButton({
  value,
  selected,
  label,
  onInput,
  onStart,
  onCommit,
  size = "md",
  testId,
}: {
  value: string;
  selected: boolean;
  label: string;
  onInput: (hex: string) => void;
  onStart: () => void;
  onCommit: () => void;
  size?: "sm" | "md";
  testId?: string;
}) {
  const active = useRef(false);
  const commit = useRef(onCommit);
  useEffect(() => {
    commit.current = onCommit;
  });
  useEffect(
    () => () => {
      if (active.current) commit.current();
    },
    []
  );
  return (
    <label
      title={label}
      className={cn(
        "relative inline-flex shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full transition-transform duration-150 focus-within:ring-2 focus-within:ring-accent-500/60 focus-within:ring-offset-2 motion-reduce:transition-none",
        size === "sm" ? "size-7" : "size-8",
        selected ? "ring-2 ring-accent-500 ring-offset-2 scale-110" : "ring-1 ring-ink-200 hover:scale-105"
      )}
      style={selected ? { background: value } : undefined}
    >
      <input
        type="color"
        value={value}
        aria-label={label}
        data-testid={testId}
        onFocus={() => {
          active.current = true;
          onStart();
        }}
        onBlur={() => {
          if (!active.current) return;
          active.current = false;
          onCommit();
        }}
        onChange={(e) => onInput(e.target.value)}
        className="absolute inset-0 size-full cursor-pointer opacity-0"
      />
      <span
        className={cn("pointer-events-none rounded-full ring-1 ring-black/10", size === "sm" ? "size-3.5" : "size-4", selected && "ring-white/80")}
        style={{ background: "conic-gradient(from 0deg, #f43f5e, #f59e0b, #22c55e, #2a70f5, #a855f7, #f43f5e)" }}
      />
    </label>
  );
}
